import React from 'react';
import {Navigate, Route, Routes} from 'react-router-dom';
import Home from './home';
import Support from './support';
import Insights from './insights';
import Unauthorized from './common/unauthorized';
import BusinessSelection from './business-selection/business-selection';
import {withPageLayout} from './navigation/page-layout';
import ProtectedBusinessSpecificRoute from './navigation/protected-business-specific-route';
import {useAuth} from './hooks/use-auth';
import PortalLoader from './common/PortalLoader';
import ViewOperationalSchedule from './orchestrator/view-operational-schedule';
import ViewForecastHistory from './history/view-forecast-history';
import ViewOperationalDashboard from './operational-dashboard/view-operational-dashboard';
import {ViewForecast} from './forecast/view-forecast';
import {ViewAccuracyDashboard} from './accuracy-dashboard/view-accuracy-dashboard';
import {InputSignal, Overrides} from './bulk-override';
import {ViewSchedule} from './orchestrator/view-schedule';
import {EditSchedule} from './orchestrator/edit-schedule';
import ViewInsights from './analytics/view-insights';
import ForecastBuilder from './forecast-builder';
import StoreMetadataLayout from './metadata/layout';
import Stores from './stores';
import {ViewInboundBridge} from './inbound-bridging';
import {ViewRuntimeTrackerDashboard} from './runtime-tracker/view-runtime-tracker-dashboard';
import CassandraChat from './cassandra';
import CassandraSidePane from './cassandra/side-pane';
import {useCassandraSidePane} from './cassandra/side-pane-context';
import Avatar from '@cloudscape-design/chat-components/avatar';
import TopNavigationBarV2 from './navigation/top-navigation-bar-v2';
import {useRouter} from './hooks/use-router';

const LayoutHome = withPageLayout(Home);
const LayoutSupport = withPageLayout(Support);
const LayoutInsights = withPageLayout(Insights);
const LayoutUnauthorized = withPageLayout(Unauthorized);
const LayoutBusinessSelection = withPageLayout(BusinessSelection);
const LayoutViewOperationalSchedule = withPageLayout(ViewOperationalSchedule);
const LayoutViewSchedule = withPageLayout(ViewSchedule);
const LayoutEditSchedule = withPageLayout(EditSchedule);
const LayoutViewForecast = withPageLayout(ViewForecast);
const LayoutViewAccuracyDashboard = withPageLayout(ViewAccuracyDashboard);
const LayoutViewInsights = withPageLayout(ViewInsights);
const LayoutViewInboundBridge = withPageLayout(ViewInboundBridge);
const LayoutOverrides = withPageLayout(Overrides);
const LayoutInputSignal = withPageLayout(InputSignal);
const LayoutViewForecastHistory = withPageLayout(ViewForecastHistory);
const LayoutViewOperationalDashboard = withPageLayout(ViewOperationalDashboard);
const LayoutForecastBuilder = withPageLayout(ForecastBuilder);
const LayoutStores = withPageLayout(Stores);
const LayoutStoreMetadata = withPageLayout(StoreMetadataLayout);
const LayoutViewRuntimeTrackerDashboard = withPageLayout(ViewRuntimeTrackerDashboard);

export default function AppV2() {
    const auth = useAuth();
    const {toggle} = useCassandraSidePane();
    const router = useRouter();

    if (!auth.isAuthenticated) {
        return <PortalLoader />;
    }

    const perms = auth.authInformation?.current?.permissions;
    const scopes = auth.authInformation?.globalScopes;
    const hasCurrent = !!auth.authInformation?.current;

    const canViewDashboard = scopes?.['operationalDashboard']?.split(',').includes('view') ?? false;
    const canManageOncall = scopes?.['oncallOperations']?.split(',').includes('manage') ?? false;
    const canViewMetadata = scopes?.['metadata']?.split(',').includes('view') ?? false;
    const canViewMerchants = scopes?.['merchants']?.split(',').includes('view') ?? false;

    return (
        <>
            <TopNavigationBarV2 />
            <div className="cassandra-below-nav">
                <div className="cassandra-below-nav-main">
                    <Routes>
                        <Route
                            path="/"
                            element={
                                hasCurrent ? <Navigate to="/forecast" replace /> : <Navigate to="/business-selection" replace />
                            }
                        />
                        <Route path="/home" element={<LayoutHome />} />
                        <Route path="/support" element={<LayoutSupport />} />
                        <Route path="/unauthorized" element={<LayoutUnauthorized />} />
                        <Route
                            path="/business-selection"
                            element={
                                <ProtectedBusinessSpecificRoute isAuthorized={true}>
                                    <LayoutBusinessSelection />
                                </ProtectedBusinessSpecificRoute>
                            }
                        />
                        <Route
                            path="/forecast"
                            element={
                                <ProtectedBusinessSpecificRoute isAuthorized={perms?.canViewForecasts || false}>
                                    <LayoutViewForecast />
                                </ProtectedBusinessSpecificRoute>
                            }
                        />
                        <Route
                            path="/forecast-builder"
                            element={
                                <ProtectedBusinessSpecificRoute isAuthorized={perms?.canViewForecasts || false}>
                                    <LayoutForecastBuilder />
                                </ProtectedBusinessSpecificRoute>
                            }
                        />
                        <Route
                            path="/accuracy-dashboard"
                            element={
                                <ProtectedBusinessSpecificRoute isAuthorized={perms?.canViewAccuracyDashboard || false}>
                                    <LayoutViewAccuracyDashboard />
                                </ProtectedBusinessSpecificRoute>
                            }
                        />
                        <Route
                            path="/runtime-tracker-dashboard"
                            element={canViewDashboard ? <LayoutViewRuntimeTrackerDashboard /> : <LayoutUnauthorized />}
                        />
                        <Route
                            path="/explain"
                            element={
                                <ProtectedBusinessSpecificRoute isAuthorized={perms?.canViewExplainability || false}>
                                    <LayoutViewInsights />
                                </ProtectedBusinessSpecificRoute>
                            }
                        />
                        <Route
                            path="/inbound-bridging"
                            element={
                                <ProtectedBusinessSpecificRoute isAuthorized={perms?.canViewForecasts || false}>
                                    <LayoutViewInboundBridge />
                                </ProtectedBusinessSpecificRoute>
                            }
                        />
                        <Route
                            path="/operational-schedule"
                            element={
                                <ProtectedBusinessSpecificRoute isAuthorized={perms?.canManageForecastWorkflows || false}>
                                    <LayoutViewOperationalSchedule />
                                </ProtectedBusinessSpecificRoute>
                            }
                        />
                        <Route
                            path="/schedule"
                            element={
                                <ProtectedBusinessSpecificRoute isAuthorized={perms?.canManageForecastWorkflows || false}>
                                    <LayoutViewSchedule />
                                </ProtectedBusinessSpecificRoute>
                            }
                        />
                        <Route
                            path="/schedule/edit"
                            element={
                                <ProtectedBusinessSpecificRoute isAuthorized={perms?.canManageForecastWorkflows || false}>
                                    <LayoutEditSchedule />
                                </ProtectedBusinessSpecificRoute>
                            }
                        />
                        <Route
                            path="/overrides"
                            element={
                                <ProtectedBusinessSpecificRoute isAuthorized={perms?.canUploadBulkOverrides || false}>
                                    <LayoutOverrides />
                                </ProtectedBusinessSpecificRoute>
                            }
                        />
                        <Route
                            path="/signals"
                            element={
                                <ProtectedBusinessSpecificRoute isAuthorized={perms?.canUploadMarketingInputs || false}>
                                    <LayoutInputSignal />
                                </ProtectedBusinessSpecificRoute>
                            }
                        />
                        <Route
                            path="/history"
                            element={
                                <ProtectedBusinessSpecificRoute isAuthorized={perms?.canViewForecasts || false}>
                                    <LayoutViewForecastHistory />
                                </ProtectedBusinessSpecificRoute>
                            }
                        />
                        <Route
                            path="/stores"
                            element={canViewMetadata || canViewMerchants ? <LayoutStores /> : <LayoutUnauthorized />}
                        />
                        <Route
                            path="/operational-dashboard"
                            element={canViewDashboard ? <LayoutViewOperationalDashboard /> : <LayoutUnauthorized />}
                        />
                        <Route path="/insights" element={canManageOncall ? <LayoutInsights /> : <LayoutUnauthorized />} />
                        <Route
                            path="/store/:programRegionId/*"
                            element={canViewMetadata ? <LayoutStoreMetadata /> : <LayoutUnauthorized />}
                        />
                        <Route
                            path="/ask"
                            element={perms?.canManageForecastWorkflows ? <CassandraChat /> : <LayoutUnauthorized />}
                        />
                    </Routes>
                </div>
                {/* Using canManageForecastWorkflows to restrict chatbot to admins for now.
                   This will be opened up to non-admin users in a future release.
                   Also limited to alpha and local stages during initial rollout. */}
                {(perms?.canManageForecastWorkflows || false) && ['alpha', 'local'].includes(auth.authInformation?.stage ?? '') && (
                    <>
                        <CassandraSidePane />
                        {router.pathname !== '/ask' && (
                            <aside className="cassandra-aside-rail">
                                <button
                                    onClick={toggle}
                                    aria-label="Toggle Ask Excelsior chat"
                                    style={{cursor: 'pointer', background: 'none', border: 'none', padding: 0}}
                                >
                                    <Avatar color="gen-ai" iconName="gen-ai" ariaLabel="Toggle Ask Excelsior chat" width={36} />
                                </button>
                            </aside>
                        )}
                    </>
                )}
            </div>
        </>
    );
}
